<template>
  <div>
    <LoadingState v-if="!status.isLoaded" :size="50" />
    <div v-if="status.isLoaded && server">
      <PageTitle>
        <div class="mr-2">{{ server.allocation.hostname }}</div>
        <HorizontalList :items="headerItems()" />
        <template #actions>
          <DropDown :items="menuItems()">
            <Btn>{{ $t("global.actions") }}</Btn>
          </DropDown>
        </template>
      </PageTitle>
      <div class="row">
        <div class="col-lg-9">
          <div class="row">
            <div class="col-lg-12 mb-4">
              <ServerGraphsCard :server="server" />
            </div>
          </div>
          <div class="row">
            <div class="col-lg-12">
              <ServerAllocationsTable
                :server-id="server.id"
                :allocations="server.allocations"
                class="mb-4"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-lg-12">
              <ServerAddonsTable :addons="server.addons" class="mb-4" />
            </div>
          </div>
        </div>
        <div class="col-lg-3">
          <Card :title="$t('global.bandwidth')" :height100="false">
            <CardDataHighlight
              :title="$t('global.used')"
              :subtitle="server.bandwidth.type"
              :data="getBandwidthUsage('used')"
            />
            <hr />
            <CardDataHighlight
              :title="$t('global.projected-billable')"
              :subtitle="server.bandwidth.type"
              :data="getBandwidthUsage('projected')"
            />
            <hr />
            <CardDataHighlight
              :title="$t('global.previous-billable')"
              :subtitle="server.bandwidth.type"
              :data="getBandwidthUsage('previous')"
            />
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import {
  getCdnControlPanelLogin,
  getContactSupport,
  getDeleteService,
  getNewRelicLogin,
  getReboot,
  getToggleEdgecastApac,
  getToggleNetworking,
} from "@/utils/serviceMenuOptions";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import CardDataHighlight from "@/components/display/card-sub-components/CardDataHighlight";
import DropDown from "@/components/display/DropDown";
import { getStatusVariant } from "@/utils/statusVariant";
import HorizontalList from "@/components/display/HorizontalList";
import LoadingState from "@/components/display/LoadingState";
import PageTitle from "@/components/display/PageTitle";
import ServerAddonsTable from "@/components/smart/tables/ServerAddonsTable";
import ServerAllocationsTable from "@/components/smart/tables/ServerAllocationsTable";
import ServerGraphsCard from "@/components/smart/cards/ServerGraphs";
import store from "@/store";

export default {
  name: "DedicatedServerDetailPage",
  components: {
    Btn,
    Card,
    CardDataHighlight,
    DropDown,
    HorizontalList,
    LoadingState,
    PageTitle,
    ServerGraphsCard,
    ServerAddonsTable,
    ServerAllocationsTable,
  },
  props: {
    dedicatedServerId: [Number, String],
  },
  setup(props, { root }) {
    const routePath = computed(() => {
      return root.$route.fullPath;
    });

    const server = computed(() => store.state.dedicatedServers.item);
    const status = computed(() => store.state.dedicatedServers.status);

    const getBandwidthUsage = (period) => {
      const bw = server.value.bandwidth?.[period];
      const billable = bw.billable;
      const total = period === "used" ? server.value.bandwidth.value : bw.total;
      return `${billable} / ${total}`;
    };

    const headerItems = () => [
      {
        key: root.$t("global.status"),
        value:
          server.value && server.value.status
            ? root.$t(`global.${server.value.status}`)
            : root.$t("global.na"),
        variant: getStatusVariant(server.value.status),
      },
      {
        key: root.$t("global.plan"),
        value:
          server.value.description && server.value.description
            ? server.value.description
            : root.$t("global.na"),
      },
      {
        key: root.$t("global.renewal"),
        value:
          server.value.billing && server.value.billing.formattedNextBillDate
            ? server.value.billing.formattedNextBillDate
            : root.$t("global.na"),
      },
      {
        key: root.$t("global.price"),
        value:
          server.value.billing && server.value.billing.price
            ? server.value.billing.price
            : root.$t("global.na"),
      },
    ];

    const menuItems = () => {
      const items = [];

      items.push(
        getCdnControlPanelLogin(server.value),
        getContactSupport(server.value),
        getNewRelicLogin(server.value),
        getReboot(server.value),
        getToggleEdgecastApac(server.value),
        getToggleNetworking(server.value),
        getDeleteService(server.value)
      );

      return items.filter((i) => i !== null);
    };

    onMounted(() => {
      store.dispatch("dedicatedServers/get", props.dedicatedServerId);
    });

    return {
      getBandwidthUsage,
      getStatusVariant,
      headerItems,
      menuItems,
      routePath,
      server,
      status,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.server-attributes {
  font-size: 0.875rem;
  .attribute {
    display: flex;
    align-items: baseline;
    @include media-breakpoint-up(md) {
      &:not(:last-child) {
        &::after {
          margin-right: 1rem;
          margin-left: 1rem;
          content: "|";
        }
      }
    }
    .field {
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
    .value {
      font-weight: bold;
    }
  }
}
</style>
